import axios from 'axios';
import { store } from '../../redux/store';
import { logout } from '../../redux/slices/authSlice';
import { authApi } from '../../redux/services/authApi';
import { setIsFetching } from '../../redux/slices/fetchSlice';
import { showErrorPage } from '../../redux/slices/coreSlice';
import { notifyError } from './toastBridge.jsx';
import extractApiErrorMessage from './extractApiErrorMessage.jsx';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const api = axios.create({
  baseURL: '/',
  withCredentials: true,
  headers: { 'X-Requested-With': 'XMLHttpRequest' },
});

api.interceptors.request.use(
  (config) => {
    const token = store.getState().auth.accessToken;
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (!config.silent) {
      store.dispatch(setIsFetching({ isFetching: true }));
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => {
    if (!response.config?.silent) {
      store.dispatch(setIsFetching({ isFetching: false }));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (originalRequest.url.includes('/refresh-token')) {
      return;
    }

    // If the server returned a Blob (common with responseType 'blob'), try to parse it to JSON/text
    try {
      const resp = error.response;
      if (resp && resp.data && typeof resp.data === 'object') {
        const isBlob = typeof Blob !== 'undefined' && resp.data instanceof Blob;
        if (isBlob) {
          const text = await resp.data.text();
          try {
            resp.data = JSON.parse(text);
          } catch (_) {
            resp.data = { error: text };
          }
        }
      }
    } catch (_) {
      // swallow parsing errors; fall back to default handling
    }

    // Background requests fail quietly (no error page, no toast).
    if (originalRequest?.silent) {
      return Promise.reject(error);
    }

    const status = error.response ? error.response.status : null;
    const suppress422Toast = Boolean(originalRequest?.suppress422Toast);

    if (status === 401) {
      if (originalRequest._retry) {
        store.dispatch(logout(false));
        return Promise.reject(error);
      }

      if (originalRequest.url.includes('/login')) {
        store.dispatch(setIsFetching({ isFetching: false }));
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResult = await store.dispatch(
          authApi.endpoints.refresh.initiate(),
        );

        if (refreshResult.data?.access_token) {
          const newAccessToken = refreshResult.data.access_token;
          processQueue(null, newAccessToken);

          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

          return api(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    store.dispatch(setIsFetching({ isFetching: false }));

    if (status === 403) {
      // notifyError({
      //   message: '403 - Nemáte oprávnenia'
      // });
      //eventBus.dispatch('navigateBack');
      store.dispatch(
        showErrorPage({ status: 403, message: error.response.data.error }),
      );
      return Promise.reject(error);
    }

    if (
      status === 404 &&
      originalRequest.method.toUpperCase() === 'GET' &&
      !originalRequest.skip404Redirect
    ) {
      store.dispatch(
        showErrorPage({ status: 404, message: error.response.data.error }),
      );
      return Promise.reject(error);
    }

    if (status === 422 && suppress422Toast) {
      return Promise.reject(error);
    }

    const errorMessage = extractApiErrorMessage(error);
    if (typeof errorMessage === 'string' && errorMessage.trim().length > 0) {
      notifyError({ message: errorMessage, duration: 7 });
    }
    return Promise.reject(error);
  },
);

export default api;
